/**
 * WalkthroughOverlay — the scripted tour's visual surface.
 *
 * Renders only while a tour is active: a dimming spotlight around the current
 * step's `data-coach` anchor (reusing the CoachOverlay spotlight technique via
 * the shared useAnchorRect hook) plus a copilot-branded card with the step's
 * title/body, a "Step N of M" indicator, and Back / Next (or Finish) / Skip
 * controls. If the anchor isn't on screen yet, the card centers itself.
 */

import React from 'react';
import { useWalkthrough } from './WalkthroughProvider';
import useAnchorRect from '../Copilot/useAnchorRect';
import './WalkthroughOverlay.css';

const CARD_W = 320;
const CARD_H_EST = 200;

export default function WalkthroughOverlay() {
  const { active, step, stepIndex, stepCount, isFirst, isLast, busy, next, back, skip } = useWalkthrough();
  const rect = useAnchorRect(active ? step?.coachId : null);

  if (!active || !step) return null;

  const pad = 6;
  const spotStyle = rect && {
    left: rect.left - pad,
    top: rect.top - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };

  // Position the card near the anchor; fall back to screen-center if no anchor.
  const cardStyle = rect
    ? {
        left: Math.min(Math.max(12, rect.left), window.innerWidth - CARD_W - 12),
        top: rect.bottom + 14 + CARD_H_EST < window.innerHeight
          ? rect.bottom + 14
          : Math.max(12, rect.top - CARD_H_EST - 14),
      }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="wt-overlay">
      {spotStyle && <div className="wt-spot" style={spotStyle} />}

      <div className="wt-card" style={cardStyle}>
        <div className="wt-card-head">
          <span className="wt-dot" />
          <span className="wt-brand">LiTeCAB Copilot</span>
          <span className="wt-progress">{stepIndex + 1} of {stepCount}</span>
        </div>

        {step.title && <div className="wt-title">{step.title}</div>}
        <div className="wt-body">{step.body}</div>

        <div className="wt-actions">
          <button className="wt-skip" onClick={skip}>Skip tour</button>
          <div className="wt-nav">
            {!isFirst && (
              <button className="wt-btn wt-btn-ghost" onClick={back} disabled={busy}>Back</button>
            )}
            <button className="wt-btn wt-btn-primary" onClick={next} disabled={busy}>
              {busy ? '…' : isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
